import { useEffect, useMemo, useState } from "react";
import { apiClient } from "../../api/client";

export type FrameOwnerInfo = { count: number; shortIds: string[] };
export type FrameOwnersMap = Record<string, FrameOwnerInfo>;

type ApiResp = { owners?: FrameOwnersMap };

export function useFrameOwners(usernames: string[] | undefined): {
  owners: FrameOwnersMap;
  loading: boolean;
  error: string | null;
} {
  const [owners, setOwners] = useState<FrameOwnersMap>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // stable key from sorted, deduped, lowercased usernames
  const key = useMemo(() => {
    if (!usernames || usernames.length === 0) return "";
    const cleaned = Array.from(
      new Set(
        usernames
          .filter((u): u is string => typeof u === "string" && u.length > 0)
          .map((u) => u.trim().toLowerCase())
          .filter((u) => u.length > 0)
      )
    ).sort();
    if (cleaned.length === 0) return "";
    // backend caps at 100; trim defensively
    return cleaned.slice(0, 100).join(",");
  }, [usernames]);

  useEffect(() => {
    if (!key) {
      setOwners({});
      setLoading(false);
      setError(null);
      return;
    }

    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await apiClient.get<ApiResp>("/api/aotd-irl/owners", {
          params: { usernames: key },
          signal: ctrl.signal,
        });
        if (ctrl.signal.aborted) return;
        setOwners(res.data?.owners ?? {});
      } catch (e: unknown) {
        if (ctrl.signal.aborted) return;
        // soft-fail — badges are decorative; never break the host UI
        setOwners({});
        const msg =
          e && typeof e === "object" && "message" in e
            ? String((e as { message?: string }).message ?? "request failed")
            : "request failed";
        setError(msg);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [key]);

  return { owners, loading, error };
}

// convenience accessor that handles case-insensitive username lookup
export function getOwnerInfo(
  owners: FrameOwnersMap,
  username: string | undefined | null
): FrameOwnerInfo | null {
  if (!username) return null;
  const direct = owners[username];
  if (direct) return direct;
  const lower = username.trim().toLowerCase();
  if (lower !== username) {
    const lc = owners[lower];
    if (lc) return lc;
  }
  // last-resort case-insensitive search
  for (const key of Object.keys(owners)) {
    if (key.toLowerCase() === lower) return owners[key];
  }
  return null;
}
